'use client'

import { CalendarClock, MapPin } from 'lucide-react'

import type { RedeemMessageKey } from '../i18n/redeem.en'
import { useRedeemT } from '../i18n/useRedeemT'
import type { IAvailabilityReason, IRedeemRecommend } from '../types/schema'

/**
 * The default heading says "that exact time", true only when the slot itself is taken. Say so when
 * the real problem is the date or the notice period — otherwise the user retries unchanged.
 * Reasons absent here (`ROOM_BUSY`, `CAPACITY`, `INVALID_INPUT`) use the default, which fits them.
 */
const COPY_BY_REASON: Partial<
  Record<IAvailabilityReason, { title: RedeemMessageKey; description: RedeemMessageKey }>
> = {
  ADVANCE_WINDOW: {
    title: 'TITLE_RECOMMEND_ADVANCE',
    description: 'DESCRIPTION_RECOMMEND_ADVANCE'
  },
  PAST: { title: 'TITLE_RECOMMEND_PAST', description: 'DESCRIPTION_RECOMMEND_PAST' },
  GIFT_CAP: { title: 'TITLE_RECOMMEND_GIFT_CAP', description: 'DESCRIPTION_RECOMMEND_GIFT_CAP' },
  OUTSIDE_HOURS: {
    title: 'TITLE_RECOMMEND_OUTSIDE_HOURS',
    description: 'DESCRIPTION_RECOMMEND_OUTSIDE_HOURS'
  }
}

const DEFAULT_COPY = {
  title: 'TITLE_RECOMMEND',
  description: 'DESCRIPTION_RECOMMEND'
} as const

interface Props {
  recommend: IRedeemRecommend
  onPick: (branchId: number, time: string) => void
}

/**
 * A normal outcome, not an error — the eGift stays valid either way, so: amber, not red.
 * Fed by the pre-flight check, or by the booking API's `create_status: false` on the rare path it
 * did not predict. Both normalised first, so this never learns which answered.
 * Every button names its own branch, so picking one always shows which branch the booking will
 * move to — no separate "different location" copy needed.
 */
export default function RecommendPanel({ recommend, onPick }: Props) {
  const t = useRedeemT()

  const copy = (recommend.reason && COPY_BY_REASON[recommend.reason]) || DEFAULT_COPY

  if (!recommend.options.length) {
    return (
      <div
        role='status'
        className='mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800'>
        <h3 className='font-bold'>{t(copy.title)}</h3>
        {/* The reason-specific body already explains what to do; only the generic case needs the
            "try another date or call us" fallback. */}
        <p className='mt-1'>
          {t(copy === DEFAULT_COPY ? 'NOTICE_RECOMMEND_NONE' : copy.description)}
        </p>
      </div>
    )
  }

  return (
    <div role='status' className='mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 md:p-5'>
      <div className='flex items-start gap-3'>
        <CalendarClock className='mt-0.5 h-5 w-5 shrink-0 text-amber-600' />
        <div className='min-w-0 flex-1'>
          <h3 className='text-sm font-bold text-amber-900'>{t(copy.title)}</h3>
          <p className='mt-1 text-sm text-amber-800'>{t(copy.description)}</p>

          <p className='mt-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-amber-700'>
            <MapPin className='h-3.5 w-3.5 shrink-0' />
            {t('LABEL_RECOMMEND_NEARBY_BRANCHES')}
          </p>

          <div className='mt-2 flex flex-wrap gap-2'>
            {recommend.options.flatMap((option) =>
              option.times.map((time) => (
                <button
                  key={`${option.branchId}-${time}`}
                  type='button'
                  onClick={() => onPick(option.branchId, time)}
                  className='flex flex-col items-start gap-0.5 rounded-xl border border-amber-300 bg-white px-4 py-2 text-left transition-colors hover:border-amber-500 hover:bg-amber-100'>
                  <span className='text-[11px] font-bold uppercase tracking-wide text-amber-700'>
                    {option.branchName}
                  </span>
                  <span className='text-sm font-bold text-amber-900'>{time}</span>
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
